import React, { useEffect, useState } from 'react';
import * as QueryHelper from '../../utils/QueryHelper';
import CheckboxTree from '../../components/connectedForm/CheckboxTree';
import { TreeComponentContextType, useAssemblyTreeContext } from './AssemblyTreeContext';

/**
 * Props for a TreeComponentSpace <T is the raw type of the nodes, U is the raw type of the root nodes>
 *
 * listItems: the list of items that make up the elements of the tree, these can be of type any because they are the result
 *   of a query so could have edges on them and typenames etc.
 * form: the form that the tree will control, this will be updated as the user navigates the tree
 * processEntities: an optional function that turns the returned entities from the list query into a list of plain entities
 *   i.e. removes edges etc. if this is not supplied then QueryHelper.processData will be used
 * constructNodes: an optional function that wraps the processed entities into nodes
 * the children getter specifies how to get the children from the root type e.g. getChildren
 */
interface TreeComponentProps<T, U> {
  listItems: any[];
  form: React.ComponentType<any>;
  subForm?: React.ComponentType<any>;
  processEntities?: (fetchedEntities: any[]) => T[];
  constructNode: (processedEntity: T) => TreeNode<T>;
  initialNodes?: RootNode<U>[];
  updatedNodes?: string[];
  childrenGetter: (root: U) => T[];
  onSelectNode: (data: T) => void;
  onExpandNode: (data: any) => void;
}

/**
 * Wraps the items in the process list with data to help construct the structure of the tree
 * value - typically maps to the entity id, this is the unique id of the node
 * rootNode - the id of the top level node that this node belongs to
 * sortOrder - defines which order the children of a node are displayed in
 * label - the display field of the node
 * showCheckBox - typically false defines whether to show a check box
 * parent - the direct ancestor of the node
 * children - the direct descendents of the node
 * className - css class of the node, typically 'activeNode' or ''
 * data - the actual entity that this node represents
 */
export interface TreeNode<T> {
  value: string;
  rootNode: number;
  sortOrder: number;
  label: string;
  showCheckbox?: boolean;
  parent: RootNode<any>;
  hasChildren?: boolean;
  children: TreeNode<T>[];
  childNodes: TreeNode<T>[];
  className: string;
  data: T;
  updated: boolean;
}

export interface RootNode<T> {
  value: string;
  rootNode: number;
  sortOrder: number;
  label: string;
  showCheckbox?: boolean;
  parent: null;
  children: TreeNode<T>[];
  className: string;
  data: T;
}

const TreeComponentSpace = <T,>(props: TreeComponentProps<T>) => {
  const {
    listItems: entities,
    initialNodes,
    updatedNodes,
    expanded,
    setExpanded,
    childrenGetter,
  } = useAssemblyTreeContext() as TreeComponentContextType;

  const { onExpandNode, constructNode: constructNodeProp, onSelectNode, subForm } = props;

  const [processedEntities, setProcessedEntities] = useState<Array<T>>([]);
  const [nodes, setNodes] = useState<Array<TreeNode<T>>>([]);
  //const [expanded, setExpanded] = useState<Array<T>>([]);
  const [selectedNode, setSelectedNode] = useState<string>();

  //step 1, we have some entities we now need to process them
  useEffect(() => {
    let localProcessedEntities: T[];
    if (props.processEntities) {
      localProcessedEntities = props.processEntities(entities);
    } else {
      localProcessedEntities = QueryHelper.processData(entities);
    }
    setProcessedEntities(localProcessedEntities);
  }, [entities]);

  const constructNode = (processedEntity: T) => {
    const childNodes = [];
    if (processedEntity?.childNodes) {
      for (const child of processedEntity.childNodes) {
        childNodes.push(constructNode(child));
      }
    }
    const node = constructNodeProp(processedEntity);
    node.children = childNodes;
    return node;
  };

  //step 2, we have the list of process entities we now need to turn them into nodes
  useEffect(() => {
    setNodes([]);
    // iterate through the initial nodes and turn them into TreeNodes including their children
    const generatedNodes = [];
    for (const rootNode of initialNodes) {
      //adds the first layer of children
      const children = childrenGetter();
      rootNode.children = children.map((child) => constructChildNodes(child));
      generatedNodes.push(rootNode);
    }
    updateStylesForSelectedComponent(generatedNodes);
    updateStyleForNodes(generatedNodes[0]?.children);
    setNodes(generatedNodes);
  }, [processedEntities]);

  const constructChildNodes = (child: TreeNode<T>) => {
    return constructNode(child);
  };

  useEffect(() => {
    if (nodes) {
      if (expanded.length === 0) {
        setExpanded(expandAll(nodes));
        if (nodes.length > 0 && nodes[0].children.length > 0) {
          const nodeId = nodes[0].children[0].value;
          if (!selectedNode) {
            setSelectedNode(nodeId);
            onSelectNode(nodeId);
          }
        }
      }
    }
  }, [nodes]);

  useEffect(() => {
    updateStylesForSelectedComponent(nodes);
  }, [selectedNode]);

  const onExpand = (expanded: Array<any>, node: { expanded: boolean; value: string }) => {
    // pass the actual node through rather than the id
    const expandedNode = nodes[0].children.find((n) => n.value == node.value);
    if (node.expanded) {
      onExpandNode(expanded, expandedNode);
    }
    setExpanded(expanded);
  };

  const expandAll = (data: any[]) => {
    const allExpanded: any[] = [];
    data.forEach((values) => {
      allExpanded.push(values.value);
    });
    return allExpanded;
  };

  const onNodeClick = (targetNode: { value: any; toString: () => any }) => {
    const nodeId = targetNode.value ?? targetNode.toString();
    setSelectedNode(nodeId);
    onSelectNode(nodeId);
  };

  const updateStylesForSelectedComponent = (allNodes: TreeNode<T>[]) => {
    const updateStyleForSelectedNode = (aNode: TreeNode<T>) => {
      if (aNode.value === selectedNode) {
        aNode.className = `activeNode ${aNode?.className ?? ''}`;
      } else {
        aNode.className = aNode.className.replace('activeNode', '').trim();
      }

      for (const childNode of aNode.children) {
        updateStyleForSelectedNode(childNode);
      }
    };

    for (const aNode of allNodes) {
      updateStyleForSelectedNode(aNode);
    }
  };

  const updateStyleForNodes = (allNodes: TreeNode<T>[]) => {
    if (!allNodes) {
      return;
    }

    const updateStyleForNode = (aNode: TreeNode<T>) => {
      if (updatedNodes?.includes(aNode.data.nodeId)) {
        aNode.className = aNode.className.replace('rct-node-not-updated', '').trim();
        if (!aNode.className.includes('rct-node-updated')) {
          aNode.className = `${aNode.className} rct-node-updated`;
        }
      } else {
        aNode.className = aNode.className.replace('rct-node-updated', '').trim();
        if (!aNode.className.includes('rct-node-not-updated')) {
          aNode.className = `${aNode.className} rct-node-not-updated`;
        }
      }
    };

    for (const node of allNodes) {
      updateStyleForNode(node);
      if (node.children.length > 0) {
        updateStyleForNodes(node.children);
      }
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        backgroundColor: `var(--grey10)`,
        padding: '16px 20px',
      }}
    >
      <div
        className="react-grid-List"
        style={{
          width: '300px',
          height: `calc(100vh - 100px)`,
          float: 'left',
          padding: '10px',
          whiteSpace: 'nowrap',
          overflowX: 'scroll',
          overflowY: 'hidden',
        }}
      >
        <div
          style={{
            height: '60%',
            overflowY: 'auto',
          }}
        >
          <CheckboxTree
            nodes={nodes}
            expanded={expanded}
            onExpand={onExpand}
            onClick={onNodeClick}
            noCascade={true}
            showExpandAll={false}
          />
        </div>
        {selectedNode && subForm && <props.subForm />}
      </div>
      <div
        style={{
          width: `calc(100% - 316px)`,
          float: 'left',
          height: `calc(100vh - 100px)`,
          overflow: 'hidden',
        }}
      >
        {selectedNode && <props.form {...props} />}
      </div>
    </div>
  );
};

export default TreeComponentSpace;
